/**
 * useCurrentTenant — react-query hook for the logged-in tenant's profile
 * (DH-023 / DH-025).
 *
 * Returns the data + loading state for `GET /api/tenant/profile`. Components
 * that need the tenant display name, slug, sector or country code should
 * call this instead of hardcoding "Demo Tenant" / "All Sectors · GB".
 *
 * JN-ADMIN-TENANT-CACHE-001 (2026-05-27) — was `staleTime: Infinity`
 * which meant a sidebar tab loaded before the platform admin saved a
 * change (e.g. hidden-sections toggle, feature flag flip) would
 * NEVER pick up the change without a full reload. Particularly bad
 * during impersonation: admin saves a change in one tab, switches to
 * the open impersonation tab, sees stale data.
 *
 * Now uses a 30-second staleTime + refetchOnWindowFocus, so switching
 * back to a tab after making admin changes in another tab picks up
 * the change within at worst a second of focus. Identity fields
 * (name / slug / sector) still don't realistically change during a
 * session — this just stops the "I changed it but nothing updated"
 * confusion when settings DO change.
 *
 * TIERS-002 (2026-06-12, Shane) — platform-admin section/tier changes
 * must reach OPEN tenant screens in seconds (the "wrong button" fix
 * can't wait for the user's next restart). 15-second background poll
 * while the tab is visible; a hidden tab catches up on focus.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { fetchTenantProfile, type TenantProfile } from './tenantApi';

export function useCurrentTenant(): UseQueryResult<TenantProfile> {
  return useQuery({
    queryKey: ['tenant', 'profile'],
    queryFn: ({ signal }) => fetchTenantProfile(signal),
    staleTime: 30 * 1000,
    refetchInterval: 15 * 1000,
    refetchOnWindowFocus: true,
    retry: false,
  });
}
